import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import {
  ClipboardList,
  Handshake,
  PackageSearch,
  Award,
} from "lucide-react";
import VideoModal from "@/components/marketing/VideoModal";
import VideoTeaserButton from "@/components/marketing/VideoTeaserButton";
import { marketingVideos } from "@/config/marketingVideos";

/**
 * /clubs — For Private Clubs sales page (iter56 redesign).
 *
 * Audience: GMs, Directors of Golf, Head Golf Professionals, Merchandise
 * Buyers at private clubs. Tone: Four Seasons / Rolex — an exclusive
 * service, not a software platform. Less information, more impact.
 *
 * Structure per spec: Hero → Why Clubs Choose Us → Workflow → Designed
 * for Clubs → Emotional Beat → Fits Your Shop → Results → Final CTA.
 * MarketingLayout provides the existing nav + footer unchanged.
 */

const LOCKER_ROOM_BG =
  "https://static.prod-images.emergentagent.com/jobs/e16202cb-95d0-411c-902c-bd0d02e3145b/images/f5bc4a7fe6e1676168c6e0591718eb78ed19e3f94cf3d783330b28fced0a79a2.png";

const TEXT_SHADOW = "0 2px 20px rgba(0,0,0,0.75)";

export default function ClubsMarketingPage() {
  return (
    <MarketingLayout>
      <Hero />
      <WhyClubsChoose />
      <WorkflowSection />
      <DesignedForClubs />
      <EmotionalBeat />
      <FitsYourShop />
      <ResultsSection />
      <FinalCta />
    </MarketingLayout>
  );
}

// ============================================================================
// HERO — full-bleed locker-room photo, left-aligned copy, subtle dark overlay
// ============================================================================
function Hero() {
  const [showVideo, setShowVideo] = useState(false);
  return (
    <section
      data-testid="clubs-hero"
      className="relative overflow-hidden min-h-[78vh] flex items-center"
    >
      <img
        src={LOCKER_ROOM_BG}
        alt="Private club locker room"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Subtle dark wash — enough for legible cream text, gentle enough to
          keep the benches and lockers reading through it. */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A14]/78 via-[#0F1A14]/48 to-[#0F1A14]/15" aria-hidden />

      <div className="relative w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32">
        <div className="max-w-2xl">
          <div
            className="text-[12px] uppercase tracking-[0.34em] font-semibold text-[#D9C68A]"
            style={{ textShadow: TEXT_SHADOW }}
            data-testid="clubs-hero-eyebrow"
          >
            For Private Clubs
          </div>
          <h1
            className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F9F8F4] leading-[1.02]"
            style={{ textShadow: TEXT_SHADOW }}
            data-testid="clubs-hero-headline"
          >
            Deliver a member experience worthy of your club.
          </h1>
          <p
            className="mt-8 text-lg sm:text-xl text-[#F9F8F4]/95 max-w-xl leading-relaxed"
            style={{ textShadow: TEXT_SHADOW }}
            data-testid="clubs-hero-subheadline"
          >
            My Crest Club helps your golf shop capture more sales, fulfill member requests, and provide concierge-level service without adding complexity to your daily operations.
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="clubs-hero-demo"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D9C68A] text-[#1A422D] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#E5D6A8] transition-colors"
            >
              Schedule a Demo <span aria-hidden>→</span>
            </Link>
            <a
              href="#why-clubs-choose"
              data-testid="clubs-hero-learn-more"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#D9C68A] text-[#F9F8F4] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#D9C68A] hover:text-[#1A422D] transition-colors"
            >
              Learn More
            </a>
            <VideoTeaserButton
              poster={marketingVideos.clubs.poster}
              onClick={() => setShowVideo(true)}
              testId="clubs-hero-watch-video"
            />
          </div>
        </div>
      </div>
      <VideoModal
        open={showVideo}
        src={marketingVideos.clubs.src}
        poster={marketingVideos.clubs.poster}
        title={marketingVideos.clubs.title}
        onClose={() => setShowVideo(false)}
      />
    </section>
  );
}

// ============================================================================
// SECTION 2 — Why Clubs Choose · 3 large cards, no icons, generous spacing
// ============================================================================
function WhyClubsChoose() {
  const cards = [
    {
      title: "Increase Member Satisfaction",
      body: "Give members a simple way to request merchandise they discover anywhere.",
    },
    {
      title: "Capture More Sales",
      body: "Turn missed opportunities into completed special orders.",
    },
    {
      title: "Simplify Operations",
      body: "Manage member requests, vendors, and order progress from one organized dashboard.",
    },
  ];
  return (
    <section
      id="why-clubs-choose"
      data-testid="clubs-why-section"
      className="bg-[#F9F8F4]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36">
        <div className="max-w-3xl">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Why Clubs Choose
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05]">
            Why Clubs Choose My Crest Club.
          </h2>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {cards.map((c, i) => (
            <article
              key={c.title}
              data-testid={`clubs-why-card-${i + 1}`}
              className="group bg-white border border-[#E5E3D8] p-10 lg:p-12 transition-all duration-300 hover:border-[#B59A5A] hover:-translate-y-1"
            >
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-8 font-serif text-2xl lg:text-3xl text-[#1A422D] leading-tight">
                {c.title}
              </h3>
              <p className="mt-6 text-[15px] leading-relaxed text-[#4A4A4A]">
                {c.body}
              </p>
              <div className="mt-10 h-px w-12 bg-[#D9C68A] group-hover:w-24 transition-all duration-300" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SECTION 3 — A Better Experience for Everyone · 2-col workflow + prose
// ============================================================================
function WorkflowSection() {
  const steps = ["Member", "Golf Shop", "Vendor", "Member Pickup"];
  return (
    <section
      data-testid="clubs-workflow-section"
      className="bg-[#1A422D] text-[#F9F8F4]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* Left — elegant workflow */}
        <div data-testid="clubs-workflow-diagram" className="flex flex-col items-center lg:items-start">
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#D9C68A]">
            The Flow
          </div>
          <div className="mt-10 w-full max-w-sm space-y-3">
            {steps.map((step, i) => (
              <React.Fragment key={step}>
                <div
                  data-testid={`clubs-workflow-step-${i + 1}`}
                  className="relative flex items-center gap-5 px-7 py-6 border border-[#D9C68A]/40 bg-[#0F2A1C] hover:border-[#D9C68A] transition-colors"
                >
                  <span className="font-serif text-2xl text-[#D9C68A] w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-xl text-[#F9F8F4] tracking-wide">
                    {step}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className="flex justify-center py-1" aria-hidden>
                    <span className="text-[#D9C68A]/60 text-2xl leading-none">↓</span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Right — prose */}
        <div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            A Better Experience for Everyone.
          </h2>
          <p className="mt-8 text-lg text-[#F9F8F4]/85 leading-relaxed max-w-xl">
            My Crest Club connects everyone involved in a special order &mdash; member, golf shop, and vendor &mdash; on one refined, tenant-isolated platform.
          </p>
          <p className="mt-4 text-lg text-[#F9F8F4]/85 leading-relaxed max-w-xl">
            Your golf shop remains in complete control of the buying decisions, the vendor relationships, and the moment the member is told, &ldquo;It&rsquo;s here.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SECTION 4 — Designed for Private Clubs · 4 premium icon cards
// ============================================================================
function DesignedForClubs() {
  const items = [
    {
      icon: <ClipboardList size={26} strokeWidth={1.4} />,
      title: "Member Requests",
      body: "Organize every merchandise request in one place.",
    },
    {
      icon: <Handshake size={26} strokeWidth={1.4} />,
      title: "Vendor Communication",
      body: "Communicate with vendors efficiently.",
    },
    {
      icon: <PackageSearch size={26} strokeWidth={1.4} />,
      title: "Special Orders",
      body: "Track every request from start to finish.",
    },
    {
      icon: <Award size={26} strokeWidth={1.4} />,
      title: "Club Branding",
      body: "Strengthen loyalty through more logo merchandise.",
    },
  ];
  return (
    <section
      data-testid="clubs-designed-section"
      className="bg-[#F9F8F4]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Designed For Private Clubs
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05]">
            Built with the pro shop in mind.
          </h2>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`clubs-designed-card-${i + 1}`}
              className="bg-white border border-[#E5E3D8] p-8 lg:p-10 hover:border-[#B59A5A] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#1A422D] text-[#D9C68A] rounded-full">
                {it.icon}
              </div>
              <h3 className="mt-8 font-serif text-xl text-[#1A422D] leading-tight">
                {it.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#4A4A4A]">
                {it.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SECTION 5 — Emotional beat · full-width cream, large centered headline
// ============================================================================
function EmotionalBeat() {
  return (
    <section
      data-testid="clubs-emotional-section"
      className="bg-[#F1EFE6] border-y border-[#E5E3D8]"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-36 sm:py-44 text-center">
        <h2
          data-testid="clubs-emotional-headline"
          className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05]"
        >
          More Than Software.
          <br />
          A Better Member Experience.
        </h2>
        <div className="mt-16 max-w-2xl mx-auto space-y-6 text-lg sm:text-xl text-[#4A4A4A] leading-relaxed">
          <p>Members no longer hear, &ldquo;We don&rsquo;t have it.&rdquo;</p>
          <p className="font-serif italic text-2xl sm:text-3xl text-[#1A422D]">
            Instead, they hear,
            <br />
            &ldquo;We&rsquo;ll take care of it.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SECTION 6 — Built Around Your Existing Golf Shop · 3-col, minimal
// ============================================================================
function FitsYourShop() {
  const columns = [
    "Works with your current buying process.",
    "No complicated implementation.",
    "Easy for staff to learn.",
  ];
  return (
    <section
      data-testid="clubs-fits-section"
      className="bg-[#F9F8F4]"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Fits Your Shop
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl text-[#1A422D] leading-[1.05]">
            Built Around Your Existing Golf Shop.
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
          {columns.map((line, i) => (
            <div
              key={line}
              data-testid={`clubs-fits-col-${i + 1}`}
              className="text-center"
            >
              <div className="mx-auto w-12 h-px bg-[#D9C68A]" />
              <p className="mt-8 font-serif text-xl lg:text-2xl text-[#1A422D] leading-snug">
                {line}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SECTION 7 — Results · 4 elegant stats, typography-forward, no counters
// ============================================================================
function ResultsSection() {
  const stats = [
    "More Special Orders",
    "Higher Member Satisfaction",
    "Better Buying Decisions",
    "Additional Merchandise Revenue",
  ];
  return (
    <section
      data-testid="clubs-results-section"
      className="bg-[#1A422D] text-[#F9F8F4]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#D9C68A]">
            Results
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Results Your Club Can Expect.
          </h2>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {stats.map((s, i) => (
            <div
              key={s}
              data-testid={`clubs-result-${i + 1}`}
              className="text-center border-l border-[#D9C68A]/25 first:border-l-0 md:first:border-l md:[&:nth-child(3n)]:border-l lg:first:border-l-0 lg:[&:nth-child(3n)]:border-l-[#D9C68A]/25 px-4 lg:px-8"
            >
              <div className="font-serif text-6xl lg:text-7xl text-[#D9C68A] leading-none">
                &mdash;
              </div>
              <p className="mt-8 font-serif text-xl lg:text-2xl text-[#F9F8F4] leading-snug">
                {s}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-16 text-center text-sm text-[#F9F8F4]/60 italic max-w-2xl mx-auto">
          Every club is different. Results reflect the qualitative outcomes clubs consistently report.
        </p>
      </div>
    </section>
  );
}

// ============================================================================
// FINAL CTA — large centered
// ============================================================================
function FinalCta() {
  return (
    <section
      data-testid="clubs-final-cta"
      className="bg-[#F9F8F4]"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-32 sm:py-40 text-center">
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05]">
          Elevate the Way Your Members Shop.
        </h2>
        <p className="mt-8 text-lg text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto">
          My Crest Club helps private clubs provide exceptional service while creating new opportunities for merchandise revenue.
        </p>
        <div className="mt-14 flex flex-wrap justify-center gap-4">
          <Link
            to="/contact"
            data-testid="clubs-final-demo"
            className="inline-flex items-center gap-2 px-9 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#0F2A1C] transition-colors"
          >
            Schedule a Demo <span aria-hidden>→</span>
          </Link>
          <Link
            to="/contact"
            data-testid="clubs-final-sales"
            className="inline-flex items-center gap-2 px-9 py-4 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Contact Sales
          </Link>
        </div>
      </div>
    </section>
  );
}
